import type { NormalizedKeyEvent } from './types';
import type React from 'react';

/**
 * Returns true when the currently focused element is an editable form control
 * (input, textarea, select, or contentEditable). Use to guard global keyboard
 * shortcuts so they don't intercept normal typing in overlays/modals.
 */
export function isEditableElementFocused(): boolean {
  const el = document.activeElement;
  if (!el) return false;
  if (
    el instanceof HTMLInputElement ||
    el instanceof HTMLTextAreaElement ||
    el instanceof HTMLSelectElement
  ) {
    return true;
  }
  if ((el as HTMLElement).contentEditable === 'true') {
    return true;
  }
  return false;
}

/**
 * Returns true while a dialog from the shared `common/Modal` is open — it paints
 * a backdrop over everything under it and owns the keyboard until it closes.
 *
 * Use alongside `isEditableElementFocused` to stand a global shortcut down: a
 * key that acts on what the backdrop covers (Space toggling the clip preview's
 * player while the clips picker is up) reads as the app doing something to a
 * surface the user cannot see.
 */
export function isModalDialogOpen(): boolean {
  return !!document.querySelector('[data-modal-backdrop]');
}

export function normalizeKeyboardEvent(
  event: KeyboardEvent | React.KeyboardEvent<Element>,
): NormalizedKeyEvent {
  const nativeEvent: KeyboardEvent =
    'nativeEvent' in event ? event.nativeEvent : event;

  return {
    code: event.code,
    key: event.key,
    meta: event.metaKey,
    ctrl: event.ctrlKey,
    shift: event.shiftKey,
    alt: event.altKey,
    repeat: event.repeat,
    nativeEvent,
    event,
  };
}
